import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, filter, map, take } from 'rxjs/operators';
import { ApiResponse, LogginService } from '@hrra/core';
import {
  getCvRegistrationPageSeoData,
  registerJobseeker,
} from 'src/app/root-store/jobseeker-store/jobseeker-registration/actions';
import {
  selectCvRegistrationPageMetaData,
  selectIsRegistrationLoadign,
  selectRegistrationFailed,
} from 'src/app/root-store/jobseeker-store/jobseeker-registration/selectors';
import { AppState } from 'src/app/root-store/state';
import { DialogService } from 'src/app/shared/services/dialog.service';
import {
  JobseekerRegistrationRequestViewModel,
  JobseekerRegistrationValidationMessage,
  JobseekerService,
  ServerValidationResult,
} from '../shared/services/jobseeker.service';
import {
  selectCurrentWebsite,
  selectPhoneCountrySettings,
  selectRecaptchaSiteKey,
} from 'src/app/root-store/selectors';
import { ProviderWebsite } from 'src/app/shared/models/provider-website';
import { MetaService } from 'src/app/shared/services/meta.service';
import { DOCUMENT } from '@angular/common';
import { FacebookService } from '@hrra/integrations';
import {
  hideCustomerChat,
  loadConfigs,
  showCustomerChatDialog,
} from 'src/app/root-store/actions';
import { Patterns } from '@hrra/core';
import { Router } from '@angular/router';
import { selectJobseekerPasswordRequirements } from 'src/app/root-store/jobseeker-store/jobseeker-profile/selectors';
import pino from 'pino';

@Component({
  selector: 'app-jobseeker-registration',
  templateUrl: './redesign/jobseeker-registration.component.html',
  styleUrls: ['./redesign/jobseeker-registration.component.scss'],
  standalone: false,
})
export class JobseekerRegistrationComponent implements OnInit, AfterViewInit {
  metaDataSubscription: Subscription;
  subscription: Subscription;

  private _logger: pino.Logger;

  get logger (){
    return this._logger;
  }

  registrationForm: UntypedFormGroup;

  // PhoneNumberFormat.E164 from google-libphonenumber, e.g. +995555123456
  public readonly phoneOutputFormat = 0;

  public readonly signInTab = 0;
  public readonly registrationTab = 1;

  isPasswordVisible = false;

  @Input() isModalOpened = false;

  registrationFailed$: Observable<boolean>;
  website$: Observable<ProviderWebsite>;
  isLoading$: Observable<boolean>;
  passwordMinLength!: number;

  recaptchaSiteKey$: Observable<string>;
  phoneCountrySettings$: Observable<{ defaultCountry: string; preferredCountries: string[] }>;

  messagetTypeErrorTextMap: { [index: number]: string };

  @Output() success = new EventEmitter<void>();

  constructor(
    private renderer2: Renderer2,
    @Inject(DOCUMENT) private _document,
    private jobseekerService: JobseekerService,
    private store: Store<AppState>,
    private dialogService: DialogService,
    private metaService: MetaService,
    private router: Router,
    loggingService: LogginService
  ) {
    this.registrationForm = new UntypedFormGroup({
      email: new UntypedFormControl(
        null,
        [Validators.required, Validators.pattern(Patterns.emailNew)],
        this.validateEmail.bind(this)
      ),
      password: new UntypedFormControl(null),
      name: new UntypedFormControl(null, [
        Validators.required,
        Validators.pattern(Patterns.personName),
      ]),
      surname: new UntypedFormControl(null, [
        Validators.required,
        Validators.pattern(Patterns.personName),
      ]),
      // number validity (invalidNumber) is validated by ngx-material-intl-tel-input
      phone: new UntypedFormControl(null, Validators.required),
      termsAndConditions: new UntypedFormControl(null, [
        Validators.requiredTrue,
      ]),
      recaptcha: new UntypedFormControl(null, Validators.required),
    });

    this.messagetTypeErrorTextMap = {
      [JobseekerRegistrationValidationMessage.EmailRegisteredButBelongsToCustomer]:
        'text.EmailRegisteredButBelongsToCustomer',
      [JobseekerRegistrationValidationMessage.EmailRegisteredButJobseekerPending]:
        'text.EmailRegisteredToAccessYourAccountPleaseLogin', // Mari may chnage her mind, commenting out instead of removing text.EmailRegisteredButJobseekerPending',
      [JobseekerRegistrationValidationMessage.EmailRegisteredToAccessYourAccountPleaseLogin]:
        'text.EmailRegisteredToAccessYourAccountPleaseLogin',
    };

    this._logger = loggingService.createLogger('jobseeker-registration-component');
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.store.dispatch(showCustomerChatDialog()), 1000);
  }

  onTabChange(value: number | string) {
    if (Number(value) === this.signInTab) {
      this.router.navigate(['/jobseeker/sign-in']);
    }
  }

  onRegister() {
    if (this.registrationForm.valid) {
      let value = this.registrationForm.value;
      const isRedirect = !this.isModalOpened;
      let request = <JobseekerRegistrationRequestViewModel>{
        Email: value.email,
        Name: value.name,
        Surname: value.surname,
        Password: value.password,
        IsAgreementChecked: value.termsAndConditions,
        PhoneNumber: value.phone,
        ReCaptchaToken: value.recaptcha, //TODO
      };

      this.store.dispatch(registerJobseeker({ payload: {request: request, isRedirect: isRedirect} }));
      this.success.emit();

    }
  }

  validateEmail({
    value,
  }: AbstractControl): Observable<ValidationErrors | null> {
    return this.jobseekerService.validateEmail(value).pipe(
      debounceTime(500),
      map((d) => {
        if (d.data.isSuccess) {
          return null;
        } else {
          return { server: d.data.items[0].message };
        }
      }),
      catchError(
        (
          response: ApiResponse<
            ServerValidationResult<JobseekerRegistrationValidationMessage>
          >
        ) => {
          if (response.error.isConnectionFailure) {
            return null;
          } else {
            return null;
            //TODO some message about failed validation?
            //return of({ server: JobseekerRegistrationValidationMessage.CannotConnectToServer })
          }
        }
      )
    );
  }

  getValidationMessage(messageType: JobseekerRegistrationValidationMessage) {
    return this.messagetTypeErrorTextMap[messageType];
  }

  ngOnInit(): void {
    // let fb = this._document.getElementById("fb-root");
    // this.renderer2.removeStyle(fb, "display");

    this.store.dispatch(getCvRegistrationPageSeoData());

    this.registrationFailed$ = this.store.pipe(
      select(selectRegistrationFailed)
    );
    this.website$ = this.store.pipe(select(selectCurrentWebsite));

    this.isLoading$ = this.store.pipe(select(selectIsRegistrationLoadign));

    this.recaptchaSiteKey$ = this.store.pipe(select(selectRecaptchaSiteKey));

    this.phoneCountrySettings$ = this.store.pipe(select(selectPhoneCountrySettings));

    this.metaDataSubscription = this.store
      .pipe(select(selectCvRegistrationPageMetaData))
      .subscribe((data) => {
        this.metaService.updateMetaData(data.website, data.metaData);
      });

    this.store
      .select(selectJobseekerPasswordRequirements)
      .pipe(
        filter((val) => val !== null),
        take(1)
      )
      .subscribe((c) => {
        this.logger.debug(`password regex: ${c.passwordRegexPattern}`);
        this.passwordMinLength = c.passwordMinLength;
        this.registrationForm
          .get('password')
          .setValidators([
            Validators.minLength(c.passwordMinLength),
            Validators.pattern(new RegExp(c.passwordRegexPattern)),
            Validators.required,
          ]);
      });
  }

  ngOnDestroy() {
    this.metaDataSubscription.unsubscribe();

    this.store.dispatch(hideCustomerChat());
  }

  onInfoPopup(text, title) {
    this.dialogService.infoPopup(text, title);
  }
}
